export const INTAKE_MAX_FILES = 10;
export const INTAKE_MAX_FILE_BYTES = 20 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

export type IntakeDocumentIssue =
  | { code: 'too_many' }
  | { code: 'unsupported_type'; name: string }
  | { code: 'too_large'; name: string }
  | { code: 'empty'; name: string };

/** Structured variant so callers can localize the message (used by the patient pages). */
export function findIntakeDocumentIssue(files: File[]): IntakeDocumentIssue | null {
  if (files.length > INTAKE_MAX_FILES) {
    return { code: 'too_many' };
  }
  const unsupported = files.find((file) => !ACCEPTED_TYPES.has(file.type));
  if (unsupported) {
    return { code: 'unsupported_type', name: unsupported.name };
  }
  const oversized = files.find((file) => file.size > INTAKE_MAX_FILE_BYTES);
  if (oversized) {
    return { code: 'too_large', name: oversized.name };
  }
  const empty = files.find((file) => file.size === 0);
  if (empty) {
    return { code: 'empty', name: empty.name };
  }
  return null;
}

export function validateIntakeDocuments(files: File[]): string | null {
  const issue = findIntakeDocumentIssue(files);
  if (!issue) return null;
  switch (issue.code) {
    case 'too_many':
      return `Choose no more than ${INTAKE_MAX_FILES} documents.`;
    case 'unsupported_type':
      return `${issue.name} is not a PDF, JPEG, or PNG document.`;
    case 'too_large':
      return `${issue.name} is larger than 20 MB.`;
    case 'empty':
      return `${issue.name} is empty.`;
  }
}

export function formatDocumentSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
